/**
 * Wallet Usage Repository
 *
 * Handles CRUD operations and real-time listeners for usage/transaction documents
 * stored at users/{uid}/wallet/usage/{usageId}
 *
 * Records are immutable (write-once) - no update method provided.
 */

import {
  DocumentSnapshot,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  deleteDoc,
  where,
} from 'firebase/firestore';
import { Paths, Page, PaginationOptions, withBackoff, setDoc, getDoc } from './firestoreUtils';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import {
  Usage,
  UsageDoc,
  UsageStatus,
  AIProvider,
  toUsageDoc,
  fromUsageDoc,
  generateWalletItemId,
} from '../types/walletFirestore';
import { shouldTagAsTest } from '../config/agenticWorkflowConfig';

// ============================================================================
// Types
// ============================================================================

export interface CreateUsageInput {
  /** Summary description (max 25 chars) */
  summary: string;
  /** Source page/feature */
  source: string;
  /** Member ID */
  memberId: string;
  /** Amount in USD */
  amountUsd: number;
  /** Amount in OLV (snapshot) */
  amountOlv: number;
  /** OLV rate at time of transaction */
  olvRateAtTime: number;
  /** AI model ID */
  modelId: string;
  /** Provider name */
  provider: AIProvider;
  /** Tags for categorization */
  tags?: string[];
  /** Initial status (defaults to 'successful') */
  status?: UsageStatus;
}

export interface ListUsageOptions extends PaginationOptions {
  /** Filter by status */
  status?: UsageStatus;
  /** Filter by provider */
  provider?: AIProvider;
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Create a new usage record (instant write, no debounce)
 * @returns The created usage with generated ID
 */
export async function createUsage(uid: string, input: CreateUsageInput): Promise<Usage> {
  addUserScopedWriteGuard(uid, 'createUsage');

  const id = generateWalletItemId();
  const timestamp = Date.now();

  const baseTags = input.tags || [];
  const tags = shouldTagAsTest() && !baseTags.includes('test example')
    ? [...baseTags, 'test example']
    : baseTags;

  const usage: Usage = {
    id,
    timestamp,
    tags,
    summary: input.summary,
    source: input.source,
    memberId: input.memberId,
    amountUsd: input.amountUsd,
    amountOlv: input.amountOlv,
    olvRateAtTime: input.olvRateAtTime,
    modelId: input.modelId,
    provider: input.provider,
    status: input.status || 'successful',
  };

  const docData = {
    ...toUsageDoc(usage),
    created_at: serverTimestamp(),
  };

  const ref = Paths.walletUsageDoc(uid, id);
  await withBackoff(() => setDoc(ref, docData));

  return usage;
}

/**
 * Get a single usage by ID
 */
export async function getUsage(uid: string, id: string): Promise<Usage | null> {
  const ref = Paths.walletUsageDoc(uid, id);
  const snap = await withBackoff(() => getDoc(ref));
  if (!snap.exists()) return null;
  return fromUsageDoc(snap.data() as UsageDoc);
}

/**
 * List usage records with pagination (newest first)
 */
export async function listUsage(
  uid: string,
  opts?: ListUsageOptions
): Promise<Page<Usage>> {
  const col = Paths.walletUsageCol(uid);

  // Build query parts
  const parts: any[] = [];

  // Filter by status if provided
  if (opts?.status) {
    parts.push(where('status', '==', opts.status));
  }

  // Filter by provider if provided
  if (opts?.provider) {
    parts.push(where('provider', '==', opts.provider));
  }

  // Always order by timestamp descending (newest first)
  parts.push(orderBy('timestamp', 'desc'));

  // Apply limit
  if (opts?.limit) {
    parts.push(limit(opts.limit));
  }

  // Build base query
  let q = query(col, ...parts);

  // Apply cursor if provided
  if (opts?.startAfter) {
    const { startAfter: fbStartAfter } = await import('firebase/firestore');
    q = query(q, fbStartAfter(opts.startAfter as DocumentSnapshot));
  }

  const snap = await withBackoff(() => getDocs(q));
  const items = snap.docs.map((d) => fromUsageDoc(d.data() as UsageDoc));
  const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;

  return { items, nextCursor };
}

/**
 * Delete a usage record (admin only, if ever needed)
 */
export async function deleteUsage(uid: string, id: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'deleteUsage');
  const ref = Paths.walletUsageDoc(uid, id);
  await withBackoff(() => deleteDoc(ref));
}

/**
 * Delete all usage records with a specific tag
 * @param uid User ID
 * @param tag Tag to filter by (e.g., "test example")
 * @returns Number of deleted usage records
 */
export async function deleteUsageByTag(uid: string, tag: string): Promise<number> {
  addUserScopedWriteGuard(uid, 'deleteUsageByTag');

  const col = Paths.walletUsageCol(uid);
  const q = query(col, where('tags', 'array-contains', tag));
  const snap = await withBackoff(() => getDocs(q));

  let deletedCount = 0;
  for (const doc of snap.docs) {
    await withBackoff(() => deleteDoc(doc.ref));
    deletedCount++;
  }

  return deletedCount;
}

// ============================================================================
// Real-time Listeners
// ============================================================================

/**
 * Subscribe to usage records with real-time updates
 * @param uid User ID
 * @param opts Options including limit and optional filters
 * @param onData Callback with updated usage list and cursor
 * @param onError Optional error callback
 * @returns Unsubscribe function
 */
export function onUsageSnapshot(
  uid: string,
  opts: { limit: number; status?: UsageStatus; provider?: AIProvider },
  onData: (items: Usage[], nextCursor: DocumentSnapshot | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const col = Paths.walletUsageCol(uid);

  // Build query parts
  const parts: any[] = [];

  // Filter by status if provided
  if (opts.status) {
    parts.push(where('status', '==', opts.status));
  }

  // Filter by provider if provided
  if (opts.provider) {
    parts.push(where('provider', '==', opts.provider));
  }

  // Order by timestamp descending
  parts.push(orderBy('timestamp', 'desc'));

  // Apply limit
  parts.push(limit(opts.limit));

  const q = query(col, ...parts);

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => fromUsageDoc(d.data() as UsageDoc));
      const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
      onData(items, nextCursor);
    },
    (error) => {
      console.error('Usage snapshot error:', error);
      onError?.(error);
    }
  );
}
